import React, { useEffect, useState } from 'react';
import { ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { fetchWorks, Work, ExternalLink, workTitle } from '../services/work';
import { useContent } from '../contexts/ContentContext';
import BackToListButton from './BackToListButton';
import ContentPageHeader from './PageHeader';
import RecentMediaSidebar from './RecentMediaSidebar';
import { formatDateYmd } from '../utils/time';

// Reusable component for cover art animation
const AnimatedCoverArt: React.FC<{
    src?: string;
    children?: React.ReactNode;
    className?: string;
}> = ({ src, children, className = "" }) => {
    const [phase, setPhase] = useState<'initial' | 'whiteout' | 'scrolling'>('initial');
    const [isTall, setIsTall] = useState(false);

    useEffect(() => {
        if (!isTall || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Sequence: 
        // 0s-1.7s: Initial (Center)
        // 1.7s: Start Whiteout (0.5s duration)
        // 2.2s: Switch to Scrolling (Top) and Fade In
        const t1 = setTimeout(() => setPhase('whiteout'), 1700);
        const t2 = setTimeout(() => setPhase('scrolling'), 2200);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [isTall]);

    const bgPosition = phase === 'initial' || phase === 'whiteout' ? 'center' : 'top center';
    const isScroll = phase === 'scrolling';

    // When scrolling starts, we want to fade the image IN (overlay OUT).
    // phase 'whiteout' -> overlay opacity 1.
    // phase 'scrolling' -> overlay opacity 0.
    const overlayOpacity = phase === 'whiteout' ? 'opacity-100' : 'opacity-0';

    return (
        <div className={`relative overflow-hidden ${className}`}>
            {/* BG Layer */}
            <div
                className={`absolute inset-0 bg-no-repeat bg-cover transition-all duration-700
                 ${isScroll ? 'animate-vertical-scroll' : ''}`}
                style={{
                    backgroundImage: src ? `url(${src})` : undefined,
                    backgroundPosition: bgPosition,
                    // Ensure background stays attached during scroll if needed, but default is fine
                }}
            />

            {/* White Overlay for transition */}
            <div className={`absolute inset-0 bg-white pointer-events-none transition-opacity duration-500 z-0 ${overlayOpacity}`} />

            {/* Content Overlay */}
            <div className="relative z-10 h-full w-full">
                {children}
            </div>

            {/* Aspect Ratio Check */}
            {src && (
                <img
                    src={src}
                    alt=""
                    className="hidden"
                    onLoad={(e) => {
                        const img = e.currentTarget;
                        // Trigger if height > width * 0.6 (same logic as before)
                        if (img.naturalHeight > img.naturalWidth * 0.6) {
                            setIsTall(true);
                        }
                    }}
                />
            )}
        </div>
    );
};


const SongDetail: React.FC<{ id?: string; work?: Work; isModal?: boolean }> = ({ id, work: initialWork, isModal = false }) => {
    const { content, language, t } = useContent();
    const [work, setWork] = useState<Work | null>(initialWork || null);
    const [loading, setLoading] = useState(!initialWork);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [mediaError, setMediaError] = useState(false);
    usePageMetadata({ title: work ? workTitle(work, t('workPage.untitled')) : t('workPage.title'), description: work?.description, image: work?.thumbnail || undefined, enabled: !isModal, type: 'article' });
    useEffect(() => {
        let active = true;
        setIframeLoaded(false); setIsPlaying(false); setMediaError(false); setError(false);
        if (initialWork) { setWork(initialWork); setLoading(false); return; }
        setWork(null); setLoading(true);
        fetchWorks().then(works => { if (active) setWork(works.find(item => item.id === id) || null); })
            .catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [id, initialWork, attempt]);
    const heading = !isModal && <ContentPageHeader sidebar title={work ? workTitle(work, t('workPage.untitled')) : undefined} actions={<BackToListButton href="/?page=gallery&tab=works" label={t('workPage.back')} />} />;
    if (loading) return <>{heading}<p role="status">{t('common.loading')}</p></>;
    if (error) return <>{heading}<div role="alert"><p>{t('workPage.error')}</p><button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div></>;
    if (!work) return <>{heading}<p role="status">{t('workPage.missing')}</p></>;
    const platformLabel = (platform: string) => content.ui[language].work.platform[platform as keyof typeof content.ui.jp.work.platform] || platform;
    const linkLabel = (link: ExternalLink) => t('workPage.link', { purpose: t(`work.purpose.${link.purpose}`), platform: platformLabel(link.platform) });
    const primaryLink = work.links[0];
    const playlist = work.format === 'playlist' || primaryLink?.url.toLowerCase().includes('playlist');
    const mediaPath = work.playback.mediaUrl;
    const mediaExtension = mediaPath ? new URL(mediaPath).pathname.toLowerCase().split('.').pop() : '';
    const video = ['mp4', 'webm'].includes(mediaExtension || '');
    const audio = ['mp3', 'wav', 'ogg'].includes(mediaExtension || '');
    const suno = work.platform === 'suno' && !playlist && work.playback.embedUrl;
    const player = suno && iframeLoaded ? <iframe src={work.playback.embedUrl!} title={workTitle(work, t('workPage.untitled'))} width="100%" height="300" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; web-share" sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="w-full rounded-lg" />
        : isPlaying && (video || audio) ? <div className="w-full p-4">{video ? <video src={mediaPath!} title={workTitle(work, t('workPage.untitled'))} controls autoPlay className="w-full max-h-96" onError={() => setMediaError(true)} /> : <audio src={mediaPath!} title={workTitle(work, t('workPage.untitled'))} controls autoPlay className="w-full" onError={() => setMediaError(true)} />}</div>
        : <AnimatedCoverArt src={work.thumbnail || undefined} className="w-full h-[300px] bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 rounded-lg">
            <div className="absolute inset-0 bg-black/40 text-white flex flex-col items-center justify-center gap-3 p-5 text-center">
                {suno ? <button className="lab-button" onClick={() => setIframeLoaded(true)}>{t('workPage.loadPlayer')}</button>
                : video || audio ? <button className="lab-button" onClick={() => setIsPlaying(true)}>{t('workPage.play')}</button>
                : !work.thumbnail ? <p>{t('workPage.noImage')}</p> : null}
                {suno && <p>{t('workPage.playerHint')}</p>}
            </div>
        </AnimatedCoverArt>;
    const card = <div className="lab-page p-5 rounded-lg border">
        <p className="lab-kicker mb-4">{t(`work.kind.${work.kind}`)} · {t(`work.format.${work.format}`)}</p>
        {primaryLink && <div className="mb-5">
            <a className="lab-button lab-work-open" href={primaryLink.url} target="_blank" rel="noopener noreferrer" title={t('workPage.newTab')}>
                <ExternalLinkIcon size={20} aria-hidden="true" /><span>{t('workPage.openWork', { title: workTitle(work, t('workPage.untitled')) })}</span>
            </a><p className="lab-label mt-2">{t('workPage.newTab')}</p>
        </div>}
        {player}
        {mediaError && <p role="alert">{t('workPage.mediaError')}<button className="lab-button" onClick={() => { setIsPlaying(false); setMediaError(false); }}>{t('common.retry')}</button></p>}
        <p className="my-5 whitespace-pre-wrap break-words leading-8">{work.description || t('workPage.noDescription')}</p>
        <div className="flex flex-wrap gap-3">{work.links.slice(1).map(link => <a key={link.url} className="lab-button" href={link.url} target="_blank" rel="noopener noreferrer">{linkLabel(link)}</a>)}</div>
        <p className="mt-4 lab-kicker">{work.publishedAt ? formatDateYmd(work.publishedAt) : t('workPage.noDate')}</p>
        <details className="mt-4"><summary>{t('detail.production')}</summary><p>{t('workPage.identity', { id: work.id })}</p></details>
    </div>;
    return <div className="lab-page lab-content-page">
        {heading}
        {isModal ? card : <div className="lg:flex gap-6"><div className="min-w-0 flex-1">{card}</div><aside className="lg:w-72 mt-8 lg:mt-0"><RecentMediaSidebar /></aside></div>}
    </div>;
};
export default SongDetail;
